import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import moment from 'moment';
import type {
  InitiateMeetingRequestType,
  InitiateMeetingResponseType,
  UpdateZoomLogRequestType,
  UpdateZoomTimestampsRequestType,
  StartCustomCall,
  UpdateConsultStatusType,
  GenericResponseType,
  ZoomSignatureRequestType,
  ZoomSignatureResponseType,
  ZoomMeetingDetailsRequestType,
  ZoomMeetingDetailsResponseType,
  InviteLinkRequestType,
  ResponseData,
  ParticipantInviteLinkResponseData,
  ParticipantInviteLinkRequestType,
} from './zoomTypes';
import type {
  createZoomService,
  UnReadMessageCount,
  ZoomParticipantDetailsResponse,
} from './zoomService';
import { PORTAL_TYPES } from '../constants/portalType';
import { REFRESH_INTERVAL } from '../constants/CommanConstants';

type ZoomService = ReturnType<typeof createZoomService>;

export const createZoomHooks = (zoomService: ZoomService) => {
  const useInitiateMeeting = () => {
    return useMutation<
      InitiateMeetingResponseType,
      Error,
      InitiateMeetingRequestType & { userId: number; portalType: string }
    >({
      mutationFn: async ({ userId, portalType, ...payload }) => {
        const data = await zoomService.initiateMeetingApi(
          userId,
          portalType,
          payload
        );
        return data;
      },
    });
  };

  const useUpdateZoomLog = () => {
    return useMutation<GenericResponseType, Error, UpdateZoomLogRequestType>({
      mutationFn: async (payload) => {
        const data = await zoomService.updateZoomLogApi(payload);
        return data;
      },
    });
  };

  const useUpdateZoomTimestamps = () => {
    return useMutation<
      GenericResponseType,
      Error,
      UpdateZoomTimestampsRequestType
    >({
      mutationFn: async (payload) => {
        const data = await zoomService.updateZoomTimestampsApi(payload);
        return data;
      },
    });
  };

  const useUpdateConsultStatus = (onSuccess: () => void, userId: number) => {
    const queryClient = useQueryClient();
    return useMutation<GenericResponseType, Error, UpdateConsultStatusType>({
      mutationFn: async (payload) => {
        const data = await zoomService.updateConsultStatus(userId, payload);
        return data;
      },
      onSuccess: (data) => {
        console.log('Custom URL call completed successfully:', data);
        queryClient.invalidateQueries({
          queryKey: ['ConsultMoreDetails'],
        });
        onSuccess();
      },
    });
  };

  const useZoomSignature = (portalType: string) => {
    return useMutation<
      ZoomSignatureResponseType,
      Error,
      ZoomSignatureRequestType
    >({
      mutationFn: async (payload) => {
        const data = await zoomService.getZoomSignature(portalType, payload);
        return data;
      },
    });
  };

  const useZoomMeetingDetails = () => {
    return useMutation<
      ZoomMeetingDetailsResponseType,
      Error,
      ZoomMeetingDetailsRequestType
    >({
      mutationFn: async (payload) => {
        const data = await zoomService.getZoomMeetingDetails(payload);
        return data;
      },
    });
  };

  const useUpdateCustomCallStartEvents = (userId: number) => {
    return useMutation<GenericResponseType, Error, StartCustomCall>({
      mutationFn: async (payload) => {
        const data = await zoomService.updateCustomCallStartEvents(
          userId,
          payload
        );
        return data;
      },
    });
  };

  const useMeetingLinkInviterpreter = () => {
    return useMutation<ResponseData, Error, InviteLinkRequestType>({
      mutationFn: async (payload) => {
        const data = await zoomService.getInterpreterInviteLink(payload);
        return data;
      },
    });
  };

  const useMeetingLink = () => {
    return useMutation<
      ParticipantInviteLinkResponseData,
      Error,
      ParticipantInviteLinkRequestType
    >({
      mutationFn: async (payload) => {
        const data = await zoomService.getPaticipentJoinLink(payload);
        return data;
      },
    });
  };

  const ensureUrlProtocol = (url: string, defaultProtocol = 'http://') => {
    if (!url) return url;

    if (!/^https?:\/\//i.test(url)) {
      return defaultProtocol + url;
    }

    return url;
  };

  const useStartZoomMeeting = (portalType: string) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const userId = Number.parseInt(localStorage.getItem('userId') ?? '0');
    const { mutate: callInitiateMeeting } = useInitiateMeeting();
    const { mutate: callUpdateZoomCallEvents } =
      useUpdateCustomCallStartEvents(userId);

    const openInNewTab = (url: string, meetingWindow?: Window | null) => {
      const meetingUrl = ensureUrlProtocol(url);
      if (meetingWindow) {
        meetingWindow.opener = null;
        meetingWindow.location.href = meetingUrl;
      } else {
        window.open(meetingUrl, '_blank', 'noopener,noreferrer');
      }
    };

    const handleExternalConsult = (
      data: InitiateMeetingResponseType,
      meetingWindow?: Window | null
    ) => {
      if (data.externalCustomUrl !== null && data.externalCustomUrl !== '') {
        openInNewTab(data.externalCustomUrl, meetingWindow);
      } else {
        meetingWindow?.close();
        setError(new Error('External url not found'));
      }
    };

    const handleQlerZoom = (
      data: InitiateMeetingResponseType,
      consultId: number,
      username: string,
      portalType: string,
      role?: string,
      meetingWindow?: Window | null
    ) => {
      if (data.meetingId && data.meetingPassword && data.zoomKey) {
        // Save portal-specific return URL
        // For facility, use base /facility route to avoid RoleProtectedRoute authentication timing issues
        if (portalType === PORTAL_TYPES.FACILITY) {
          let returnUrl = '';
          if (role?.toLowerCase() === 'facility user') {
            returnUrl = `${globalThis.location.origin}/facility/request`;
          } else if (role?.toLowerCase() === 'case manager') {
            returnUrl = `${globalThis.location.origin}/facility/consults`;
          }
          localStorage.setItem('zoom-return-url', returnUrl);
        }

        // Signal any existing Zoom windows for this consultId to close
        console.log(
          'Signaling previous Zoom sessions to close for consultId:',
          consultId
        );
        localStorage.setItem(`closeZoom-${consultId}`, Date.now().toString());

        // Remove signal after short delay
        setTimeout(() => {
          localStorage.removeItem(`closeZoom-${consultId}`);
        }, 500);

        if (portalType === PORTAL_TYPES.FACILITY) {
          console.log('facility user joining zoom call');
          globalThis.location.href = `${globalThis.location.origin}/zoom-call/0/${username}/${data.meetingId}/${data.meetingPassword}/${consultId}/${btoa(data.zoomKey)}`;
        } else {
          localStorage.setItem(`meetingUrl-${consultId}`, data.meetingId);
          const url = `${globalThis.location.origin}/zoom-call/1/${username}/${data.meetingId}/${data.meetingPassword}/${consultId}/${btoa(data.zoomKey)}`;
          console.log('Opening new Zoom session for consultId:', consultId);

          openInNewTab(url, meetingWindow);
          if (meetingWindow) {
            sessionStorage.setItem(`zoomWindow-${consultId}`, 'opened');
          }
        }
      } else {
        meetingWindow?.close();
        setError(new Error('Meeting details not found'));
      }
    };

    const handleCustomLink = (
      data: InitiateMeetingResponseType,
      payload: StartCustomCall,
      meetingWindow?: Window | null
    ) => {
      const hasCustomLink = data.customLink !== null && data.customLink !== '';

      if (!hasCustomLink) {
        meetingWindow?.close();
        return;
      }

      if (data.useZoomDesktop) {
        openInNewTab(data.customLink!, meetingWindow);
      } else {
        payload.customCallStartTime = moment
          .utc()
          .format('YYYY-MM-DDTHH:mm:ss');
        openInNewTab(data.customLink!, meetingWindow);
      }
    };

    const startZoomMeeting = async (
      userId: number,
      consultId: number,
      username: string,
      role?: string
    ) => {
      setLoading(true);
      setError(null);
      const meetingWindow =
        portalType === PORTAL_TYPES.CLINICAL
          ? globalThis.open('', '_blank')
          : null;

      try {
        callInitiateMeeting(
          {
            userId,
            consultId,
            providerId: userId,
            portalType: portalType,
          },
          {
            onSuccess: (data) => {
              const updateZoomCallEventsPayload: StartCustomCall = {
                consultId,
                userId,
                sessionStatus: 'Call Started',
                waitEndTime: moment.utc().format('YYYY-MM-DDTHH:mm:ss'),
                customCallStartTime: moment.utc().format('YYYY-MM-DDTHH:mm:ss'),
              };

              if (portalType === PORTAL_TYPES.CLINICAL) {
                callUpdateZoomCallEvents(updateZoomCallEventsPayload);
              }

              const isZoomDesktopWithUrl =
                data.useZoomDesktop && !!data.zoomDesktopUrl;

              if (data.isExternalConsult === true) {
                handleExternalConsult(data, meetingWindow);
                return;
              }

              if (
                isZoomDesktopWithUrl &&
                portalType === PORTAL_TYPES.FACILITY
              ) {
                globalThis.open(
                  data.zoomDesktopUrl!,
                  '_blank',
                  'noopener,noreferrer'
                );
              } else if (
                isZoomDesktopWithUrl &&
                portalType === PORTAL_TYPES.CLINICAL
              ) {
                handleQlerZoom(
                  data,
                  consultId,
                  username,
                  portalType,
                  role,
                  meetingWindow
                );
              } else if (data.qlerZoom) {
                handleQlerZoom(
                  data,
                  consultId,
                  username,
                  portalType,
                  role,
                  meetingWindow
                );
              } else {
                handleCustomLink(
                  data,
                  updateZoomCallEventsPayload,
                  meetingWindow
                );
              }
            },
            onError: (error: Error) => {
              meetingWindow?.close();
              setError(error);
            },
          }
        );
      } catch (error) {
        meetingWindow?.close();
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    };

    return { startZoomMeeting, loading, error };
  };

  const useGetCompleteUnReadNotificationCount = (
    userId: number,
    conusltId: number,
    portalType: string
  ) => {
    const { data, isLoading, isError, error } = useQuery<UnReadMessageCount>({
      queryKey: ['UnreadCount'],
      queryFn: async () => {
        const data = await zoomService.getUnReadCountNotification(
          userId,
          conusltId,
          portalType
        );
        return data;
      },

      staleTime: 0,
      gcTime: 0,
      refetchInterval: REFRESH_INTERVAL,
    });

    return {
      UnreadCount: data,
      isLoading,
      isError,
      error,
    };
  };

  const useGetZoomParticipantJoinedDetails = (
    consultId: number,
    enabled: boolean
  ) => {
    const { data, isLoading, isError, error } =
      useQuery<ZoomParticipantDetailsResponse>({
        queryKey: ['zoomParticipant', consultId],
        queryFn: async () => {
          return await zoomService.getZoomParticipantJoinedDetails(consultId);
        },

        enabled,

        refetchInterval: (query) => {
          const data = query.state.data;

          // stop polling when timedOut is false
          if (data?.timedOut === false) {
            return false;
          }

          return 1000;
        },
        refetchIntervalInBackground: true,
        staleTime: 0,
        gcTime: 0,
      });

    return {
      zoomParticipant: data,
      isLoading,
      isError,
      error,
    };
  };

  return {
    useInitiateMeeting,
    useUpdateZoomLog,
    useUpdateZoomTimestamps,
    useUpdateConsultStatus,
    useZoomSignature,
    useZoomMeetingDetails,
    useUpdateCustomCallStartEvents,
    useMeetingLinkInviterpreter,
    useMeetingLink,
    useStartZoomMeeting,
    ensureUrlProtocol,
    useGetCompleteUnReadNotificationCount,
    useGetZoomParticipantJoinedDetails,
  };
};
